'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { Search, ArrowUp, ArrowDown, X, MapPin } from 'lucide-react';
import type { LocationData } from '@/types/locations';

const LeafletMap = dynamic(() => import('@/components/location/LeafletMap'), { ssr: false });

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(amount);
}

type SortKey = 'name' | 'region' | 'avgHousePrice' | 'annualLivingCosts' | 'retirementNumber' | 'totalNetWorth';
type SortDir = 'asc' | 'desc';

function SortIcon({ column, sortKey, sortDir }: { column: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (sortKey !== column) return null;
  return sortDir === 'asc' ? <ArrowUp className="w-4 h-4 inline ml-1" /> : <ArrowDown className="w-4 h-4 inline ml-1" />;
}

function TableHeader({ column, label, icon, align, sortKey, sortDir, onClick }: {
  column: SortKey;
  label: string;
  icon?: React.ReactNode;
  align?: 'left' | 'right';
  sortKey: SortKey;
  sortDir: SortDir;
  onClick: (key: SortKey) => void;
}) {
  return (
    <th
      className={`text-${align} p-4 font-bold cursor-pointer hover:bg-gray-100 select-none`}
      onClick={() => onClick(column)}
    >
      <span className={`inline-flex items-center ${align === 'right' ? 'justify-end' : 'justify-start'} gap-1`}>
        {icon && icon}
        {label}
        <SortIcon column={column} sortKey={sortKey} sortDir={sortDir} />
      </span>
    </th>
  );
}

export default function LocationsClient({ locations }: { locations: LocationData[] }) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const filteredLocations = useMemo(() => {
    if (!search.trim()) return null;
    const query = search.toLowerCase();
    return locations.filter(loc =>
      loc.name.toLowerCase().includes(query) ||
      loc.region?.toLowerCase().includes(query) ||
      loc.type.toLowerCase().includes(query)
    );
  }, [search, locations]);

  const sortedLocations = useMemo(() => {
    const data = filteredLocations || locations;
    return [...data].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortKey === 'totalNetWorth') {
        aVal = a.avgHousePrice + a.retirementNumber;
        bVal = b.avgHousePrice + b.retirementNumber;
      } else {
        aVal = a[sortKey as keyof LocationData] as string | number;
        bVal = b[sortKey as keyof LocationData] as string | number;
      }
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredLocations, locations, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleClear = () => setSearch('');

  return (
    <>
      {/* Search */}
      <div className="mb-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search for a location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-12 py-4 text-lg border-2 border-[#1a1a1a] focus:outline-none focus:border-positive-400"
          />
          {search && (
            <button
              onClick={handleClear}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {locations.length} UK locations available - click column headers to sort
        </p>
      </div>

      {/* Map */}
      {!search.trim() && locations.filter(loc => loc.coordinates).length > 0 && (
        <LeafletMap locations={locations} />
      )}

      {/* Table */}
      <div className="overflow-x-auto border-2 border-[#1a1a1a]">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b-2 border-[#1a1a1a]">
              <TableHeader column="name" label="Location" icon={<MapPin className="w-4 h-4" />} align="left" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="region" label="Region" align="left" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="avgHousePrice" label="Avg House Price" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="annualLivingCosts" label="Living Costs" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="retirementNumber" label="Avg retirement number @ 4% SWR" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
              <TableHeader column="totalNetWorth" label="Avg total net worth required" sortKey={sortKey} sortDir={sortDir} onClick={handleSort} />
            </tr>
          </thead>
          <tbody>
            {sortedLocations.map(location => (
              <tr key={location.slug} className="border-b border-[#1a1a1a] hover:bg-gray-50">
                  <td className="p-4 text-left">
                    <Link
                      href={`/locations/${location.slug}/how-much-do-i-need-to-retire`}
                      className="text-positive-600 hover:underline font-medium"
                    >
                      {location.name}
                    </Link>
                    <span className="text-gray-400 text-sm ml-2 capitalize">({location.type})</span>
                  </td>
                  <td className="p-4 text-left text-gray-600">{location.region ? location.region.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '-'}</td>
                <td className="text-right p-4 text-gray-600">{formatCurrency(location.avgHousePrice)}</td>
                <td className="text-right p-4 text-gray-600">{formatCurrency(location.annualLivingCosts)}</td>
                <td className="text-right p-4 font-bold text-positive-700">{formatCurrency(location.retirementNumber)}</td>
                <td className="text-right p-4 font-bold text-positive-700">{formatCurrency(location.avgHousePrice + location.retirementNumber)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-gray-500 mt-4">
        Calculations include UK State Pension (2026/27: £12,548/yr at age 67)
      </p>
    </>
  );
}
